import type { adminViews } from '@cwos/api-client';
import { UNKNOWN_POPULATION, narrowedTo, ORGANIZATION, type Population } from '../figureState.js';
import { mayListWorkers } from '../permissions.js';
import { scopeNoteFor, statedPopulation, type ScopeReader } from '../readScope.js';

/**
 * Pure derivations for the Team live board (doc 01 A3.1). Kept DOM-free so the
 * label/format rules are unit-tested independently of rendering.
 */

/** "Working now" is the default during org business hours; else "Everyone"/"Clients". */
export type BoardView = 'working' | 'everyone' | 'clients';

export function normalizeView(raw: string | undefined): BoardView {
  if (raw === 'everyone') return 'everyone';
  if (raw === 'clients') return 'clients';
  return 'working';
}

/** Compact open-duration label for a live session (minutes → "Hh Mm" / "Mm"). */
export function durationLabel(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

/**
 * A worker's live check-in verification (doc 04 §20 board column). The workforce
 * board exposes only a coarse `locationStatus` string; when it is a known
 * `location_verification` value the screen renders a VerificationBadge, else it
 * shows a plain "on the clock" pill. This helper decides which — never guessing
 * a verified state from a missing reading.
 */
const KNOWN_VERIFICATIONS = new Set([
  'verified_inside',
  'verified_outside',
  'recorded_no_geofence',
  'low_accuracy',
  'unavailable_no_fix',
  'unavailable_permission_denied',
  'unavailable_policy_off',
  'attested_by_worker',
]);

export function isKnownVerification(status: string | null): boolean {
  return status !== null && KNOWN_VERIFICATIONS.has(status);
}

/** Total currently-clocked-in count from the board (defensive). */
export function activeCount(board: adminViews.WorkforceBoard | undefined): number {
  return board?.currentlyClockedIn ?? 0;
}

/**
 * The board's headline, with its SCOPE said out loud.
 *
 * `currentlyClockedIn` is computed over whatever the reader reaches, and until
 * the API said which that was, a supervisor's board reported "3 on the clock"
 * in the same words an administrator's board reported the whole organisation's
 * forty. Every row was true and the sentence was not — a narrowed figure
 * reading as an organisation-wide one is the same defect as a withheld value
 * reading as an absence, and it gets the same treatment: name the frame rather
 * than leave the reader to infer it.
 *
 * Refusing the read instead would be worse. A supervisor's own team is exactly
 * what this screen is for.
 */
export function clockedInLabel(board: adminViews.WorkforceBoard | undefined): string {
  const n = activeCount(board);
  if (board?.scope === 'caseload') {
    return `${n} of your team on the clock`;
  }
  return `${n} on the clock`;
}

/**
 * The board's EMPTY state, with the same frame said out loud.
 *
 * `clockedInLabel` names the population on the non-empty path and the empty
 * path skipped it entirely: a supervisor whose own team happened to be idle was
 * shown "All quiet — nobody is clocked in right now", which is a statement
 * about the whole organization made from a caseload board. It is the identical
 * defect one branch over, and it is the more dangerous of the two — a zero is
 * exactly where a reader stops asking.
 *
 * Returns the two halves separately because the empty component takes them
 * separately, and pairs them here so a screen cannot use one without the other.
 */
export interface BoardEmptyCopy {
  heading: string;
  message: string;
}

export function emptyBoardCopy(board: adminViews.WorkforceBoard | undefined): BoardEmptyCopy {
  if (board?.scope === 'caseload') {
    return {
      heading: 'Nobody on your team is clocked in',
      message:
        'This board covers the workers assigned to you. Other people in the organization may be working — seeing them needs an organization-wide permission.',
    };
  }
  return { heading: 'All quiet', message: 'Nobody is clocked in right now.' };
}

/**
 * WHOSE ROSTER THE "EVERYONE" TAB IS SHOWING.
 *
 * `GET /directory/workers` states no `scope`, and it does not need one, because
 * it does not narrow: `guard(request, reply, 'directory:read')` and then
 * `mayReadWorkerIdentity` — `can(authz, 'user:read')` — and its own comment ends
 * "So the refusal names the key. Every 200 this route returns is COMPLETE"
 * (directory/routes.ts:518). The route considered narrowing to the supervisor
 * edge and refused to, in as many words: "a one-row list under a heading that
 * says 'Everyone' is a false statement about the organization".
 *
 * So the frame is decided entirely by the two keys, and the server decides them
 * with `can()` at ORGANIZATION scope. That is the question this asks. It is a
 * reconstruction rather than a stated frame — the exit is the same one
 * `readScope.ts` names for its last mirror, a `scope` on the payload — but it
 * reconstructs the predicate from the same function and the same default scope
 * the route uses, so it cannot answer differently for the reason the flattened
 * version did.
 *
 * `mayListWorkers` is asked with `can` rather than `has` on purpose. It is the
 * ONE client-side statement of the two-key rule (`features/permissions.ts`) and
 * stays the one statement; what changes here is which of the two questions it is
 * put to. `has` is question 1 — may I open the door — and it is right for the
 * `enabled:` of the roster query, where a permissive answer costs a 403 and a
 * strict one costs a screen the server would have served. This is question 2,
 * and a program-confined `user:read` is `true` to the first and `false` to the
 * second.
 */
function rosterPopulation(reader: ScopeReader): Population {
  // `can()` is default-deny, so it cannot tell "not held" from "not read yet".
  // Answering the second with the first would put a permission sentence in front
  // of an org_admin on every cold load.
  if (reader.isLoading || reader.isError) return UNKNOWN_POPULATION;
  // The reader itself, not a function wrapping it. `mayListWorkers` takes an
  // OBJECT carrying `can` on purpose: a `(key) => boolean` is assignable to a
  // wider `(key, scope?) => boolean` by ordinary contravariance, so the
  // function-shaped migration would have compiled everywhere and changed
  // nothing. The object shape is what made typecheck name this very line.
  if (mayListWorkers(reader)) return ORGANIZATION;
  return narrowedTo('nothing — naming people needs the directory permission');
}

/**
 * AND THE CLIENTS TAB, WHICH IS THE SAME ROUTE SHAPE AND WAS THE SAME PROMISE.
 *
 * `GET /directory/clients` is `guard(request, reply, 'directory:read')` and then
 * `listClients` unfiltered (directory/routes.ts:765) — one org-scope `can()` and
 * no narrowing. The route's own header says why a narrowed version would need a
 * declared scope: "the admin Team board renders this list under the heading
 * 'Clients', and a partial list under that heading is a false statement about
 * the organization exactly like the one-row roster above."
 *
 * This branch returned `null` unconditionally, so the summary promised "That's
 * everything" over the Refused panel `Clients` renders for a reader holding no
 * `directory:read` at all, and over the 403 a program-confined one meets. It was
 * not one of the two branches this lane was sent to reconcile; it is the third
 * arm of the same function and the identical predicate, and leaving it would
 * have restored the "one function, two philosophies" problem with a new pair.
 */
function clientRosterPopulation(reader: ScopeReader): Population {
  if (reader.isLoading || reader.isError) return UNKNOWN_POPULATION;
  if (reader.can('directory:read')) return ORGANIZATION;
  return narrowedTo('nothing — the client directory needs the directory permission');
}

/**
 * What the mandatory FilterSummary must NOT promise on this screen.
 *
 * Returns the population that IS shown when the reader does not reach all of it,
 * else null. Without this the summary printed "That's everything — no hidden
 * filters" over a caseload board and over a roster the reader may not hold the
 * key for.
 *
 * ONE FUNCTION, ONE PHILOSOPHY. The two branches used to answer from two
 * different kinds of evidence: `working` read the frame the SERVER stated on the
 * payload it framed, and `everyone` re-derived one from `/auth/me`'s FLATTENED
 * key list. The second is the defect — a `user:read` devolved to one program is
 * `true` in that list, so the summary promised a complete organization roster to
 * a reader the server was about to refuse outright. Both branches now resolve a
 * `Population` and both hand it to `scopeNoteFor`, so there is one vocabulary
 * and one place the phrase is chosen.
 *
 * AND AN UNREAD FRAME IS NOT AN ORGANIZATION-WIDE ONE. `board?.scope === 'caseload'`
 * made `undefined` — in flight, failed, or never asked because the reader lacks
 * `admin-views:read` — resolve to `null`, and `null` is what licenses "That's
 * everything". The strongest completeness claim this product makes was being
 * printed above a skeleton, above an error panel, and above the Refused panel
 * `LiveBoard` renders for a reader with no `admin-views:read` at all.
 * `statedPopulation` already spells that case `unknown`, which is the third of
 * the three frames `figureState` has and the reason there are three.
 */
export function boardNarrowedBy(
  view: BoardView,
  board: adminViews.WorkforceBoard | undefined,
  reader: ScopeReader,
): string | null {
  if (view === 'working') {
    // The SERVER's own word, off the payload the count came in. `undefined` is
    // `unknown`, never `organization`.
    return scopeNoteFor(statedPopulation(board?.scope, 'the workers assigned to you'));
  }
  if (view === 'everyone') {
    return scopeNoteFor(rosterPopulation(reader));
  }
  return scopeNoteFor(clientRosterPopulation(reader));
}
